// Trading page logic
import wsManager from './websocket.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
let currentMarket = 'BTC-USDT';
let currentSide = 'buy';
let orderbook = { bids: [], asks: [] };
let tvWidget = null;

// Custom Modal Function
function showModal(title, message, type = 'info') {
  // Remove existing modal if any
  const existingModal = document.getElementById('customModal');
  if (existingModal) {
    existingModal.remove();
  }

  // Create modal HTML
  const modal = document.createElement('div');
  modal.id = 'customModal';
  modal.className = 'modal-overlay';
  
  // Icon based on type
  let icon = '';
  switch(type) {
    case 'info':
      icon = '<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
      break;
    case 'warning':
      icon = '<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>';
      break;
    case 'success':
      icon = '<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
      break;
    case 'error':
      icon = '<svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>';
      break;
  }
  
  modal.innerHTML = `
    <div class="modal-content">
      <div class="modal-icon ${type}">
        ${icon}
      </div>
      <h3 class="text-2xl font-bold text-center mb-2">${title}</h3>
      <p class="text-neutral-400 text-center mb-6">${message}</p>
      <button onclick="document.getElementById('customModal').remove()" class="btn-primary w-full">
        Got it
      </button>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  // Close on overlay click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.remove();
    }
  });
  
  // Close on Escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape') {
      modal.remove();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);
}

// Initialize trading page
document.addEventListener('DOMContentLoaded', () => {
  // Get market from URL
  const urlParams = new URLSearchParams(window.location.search);
  const marketParam = urlParams.get('market');
  if (marketParam) {
    currentMarket = marketParam;
  }
  
  updateMarketDisplay(); // Update market pair display
  initializeTradingUI();
  initializeTradingViewChart();
  initializePriceTicker(); // Initialize TradingView price ticker
  subscribeToMarketData();
  setupOrderForm();
});

function initializeTradingViewChart() {
  // Map market pair to TradingView symbol
  const symbolMap = {
    'BTC-USDT': 'BINANCE:BTCUSDT',
    'ETH-USDT': 'BINANCE:ETHUSDT',
    'BTC-ETH': 'BINANCE:ETHBTC'
  };

  const tvSymbol = symbolMap[currentMarket] || 'BINANCE:BTCUSDT';

  // Wait for TradingView library to load
  if (typeof TradingView !== 'undefined') {
    tvWidget = new TradingView.widget({
      "autosize": true,
      "symbol": tvSymbol,
      "interval": "15",
      "timezone": "Etc/UTC",
      "theme": "dark",
      "style": "1",
      "locale": "en",
      "toolbar_bg": "#1a1a1a",
      "enable_publishing": false,
      "hide_top_toolbar": false,
      "hide_legend": false,
      "save_image": false,
      "container_id": "tradingview_widget",
      "backgroundColor": "#1a1a1a",
      "gridColor": "rgba(255, 255, 255, 0.06)",
      "allow_symbol_change": true,
      "studies": [
        "MASimple@tv-basicstudies"
      ],
      "show_popup_button": true,
      "popup_width": "1000",
      "popup_height": "650"
    });
  }
}

function initializePriceTicker() {
  console.log('Initializing price ticker...');
  // Fetch real-time price from Binance REST API (more reliable than WebSocket)
  fetchPriceData();
  
  // Update price every 2 seconds
  setInterval(fetchPriceData, 2000);
}

async function fetchPriceData() {
  try {
    console.log('Fetching price data for market:', currentMarket);
    const symbolMap = {
      'BTC-USDT': 'BTCUSDT',
      'ETH-USDT': 'ETHUSDT',
      'BTC-ETH': 'ETHBTC'
    };
    
    const symbol = symbolMap[currentMarket] || 'BTCUSDT';
    console.log('Binance symbol:', symbol);
    
    let data;
    
    try {
      // Try to fetch from Binance API
      const response = await fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${symbol}`, {
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error('API response not OK');
      }
      
      data = await response.json();
      console.log('Price data received from Binance:', data);
    } catch (fetchError) {
      console.warn('Binance API unavailable, using mock data:', fetchError.message);
      
      // Fallback to mock data
      const mockData = {
        'BTCUSDT': {
          lastPrice: '43250.50',
          priceChangePercent: '2.45',
          highPrice: '43890.00',
          lowPrice: '42100.00',
          volume: '28453.25',
          quoteVolume: '1234567890.50'
        },
        'ETHUSDT': {
          lastPrice: '2285.75',
          priceChangePercent: '1.85',
          highPrice: '2310.00',
          lowPrice: '2240.00',
          volume: '145234.50',
          quoteVolume: '332145678.90'
        },
        'ETHBTC': {
          lastPrice: '0.05285',
          priceChangePercent: '-0.65',
          highPrice: '0.05320',
          lowPrice: '0.05250',
          volume: '12453.20',
          quoteVolume: '658.45'
        }
      };
      
      data = mockData[symbol] || mockData['BTCUSDT'];
      console.log('Using mock data:', data);
    }
    
    // Update current price
    const currentPriceEl = document.getElementById('currentPrice');
    if (currentPriceEl && data.lastPrice) {
      const price = parseFloat(data.lastPrice);
      const formatted = `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      console.log('Setting price to:', formatted);
      currentPriceEl.textContent = formatted;
    } else {
      console.error('currentPrice element not found or no lastPrice in data');
    }
    
    // Update price change
    const priceChangeEl = document.getElementById('priceChange');
    if (priceChangeEl && data.priceChangePercent) {
      const change = parseFloat(data.priceChangePercent);
      priceChangeEl.textContent = `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
      priceChangeEl.className = change >= 0 ? 'badge-success' : 'badge-error';
    }
    
    // Update 24h high
    const high24hEl = document.getElementById('high24h');
    if (high24hEl && data.highPrice) {
      const high = parseFloat(data.highPrice);
      high24hEl.textContent = `$${high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    // Update 24h low
    const low24hEl = document.getElementById('low24h');
    if (low24hEl && data.lowPrice) {
      const low = parseFloat(data.lowPrice);
      low24hEl.textContent = `$${low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    // Update 24h volume
    const volume24hEl = document.getElementById('volume24h');
    if (volume24hEl && data.volume) {
      const volume = parseFloat(data.volume);
      volume24hEl.textContent = volume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    
    // Update 24h volume USDT
    const volumeUsdt24hEl = document.getElementById('volumeUsdt24h');
    if (volumeUsdt24hEl && data.quoteVolume) {
      const volumeUsdt = parseFloat(data.quoteVolume);
      const formatted = volumeUsdt >= 1000000 
        ? `$${(volumeUsdt / 1000000).toFixed(1)}M` 
        : `$${(volumeUsdt / 1000).toFixed(1)}K`;
      volumeUsdt24hEl.textContent = formatted;
    }
  } catch (error) {
    console.error('Error fetching price data:', error);
  }
}

function updateMarketDisplay() {
  const marketInfo = {
    'BTC-USDT': { pair: 'BTC/USDT', description: 'Bitcoin / Tether' },
    'ETH-USDT': { pair: 'ETH/USDT', description: 'Ethereum / Tether' },
    'BTC-ETH': { pair: 'BTC/ETH', description: 'Bitcoin / Ethereum' }
  };
  
  const info = marketInfo[currentMarket] || marketInfo['BTC-USDT'];
  
  const marketPairEl = document.getElementById('marketPair');
  const marketDescriptionEl = document.getElementById('marketDescription');
  
  if (marketPairEl) {
    marketPairEl.textContent = info.pair;
  }
  
  if (marketDescriptionEl) {
    marketDescriptionEl.textContent = info.description;
  }
}

function changeMarket(newMarket) {
  // Unsubscribe from old market
  wsManager.unsubscribe(`market.${currentMarket}.orderbook`);
  wsManager.unsubscribe(`market.${currentMarket}.trades`);
  wsManager.unsubscribe(`market.${currentMarket}.ticker`);
  
  // Update current market
  currentMarket = newMarket;
  
  // Update market display
  updateMarketDisplay();
  
  // Update TradingView chart
  const symbolMap = {
    'BTC-USDT': 'BINANCE:BTCUSDT',
    'ETH-USDT': 'BINANCE:ETHUSDT',
    'BTC-ETH': 'BINANCE:ETHBTC'
  };
  
  if (tvWidget && tvWidget.chart) {
    tvWidget.chart().setSymbol(symbolMap[newMarket] || 'BINANCE:BTCUSDT');
  }
  
  // Fetch new market price immediately
  fetchPriceData();
  
  // Subscribe to new market
  subscribeToMarketData();
}

function initializeTradingUI() {
  // Buy/Sell tab switching
  const buyTab = document.getElementById('buyTab');
  const sellTab = document.getElementById('sellTab');
  const submitButton = document.getElementById('submitOrder');
  
  buyTab.addEventListener('click', () => {
    currentSide = 'buy';
    buyTab.classList.add('bg-success-600');
    buyTab.classList.remove('bg-neutral-700');
    sellTab.classList.add('bg-neutral-700');
    sellTab.classList.remove('bg-error-600');
    submitButton.textContent = 'Buy BTC';
    submitButton.classList.remove('btn-sell');
    submitButton.classList.add('btn-buy');
  });
  
  sellTab.addEventListener('click', () => {
    currentSide = 'sell';
    sellTab.classList.add('bg-error-600');
    sellTab.classList.remove('bg-neutral-700');
    buyTab.classList.add('bg-neutral-700');
    buyTab.classList.remove('bg-success-600');
    submitButton.textContent = 'Sell BTC';
    submitButton.classList.remove('btn-buy');
    submitButton.classList.add('btn-sell');
  });
  
  // Order type tabs
  const tabs = document.querySelectorAll('[data-tab]');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => t.classList.remove('tab-active'));
      tab.classList.add('tab-active');
    });
  });
}

function subscribeToMarketData() {
  // Subscribe to orderbook updates
  wsManager.subscribe(`market.${currentMarket}.orderbook`, handleOrderbookUpdate);
  
  // Subscribe to trades
  wsManager.subscribe(`market.${currentMarket}.trades`, handleTradeUpdate);
  
  // Subscribe to ticker
  wsManager.subscribe(`market.${currentMarket}.ticker`, handleTickerUpdate);
}

function handleOrderbookUpdate(data) {
  if (!data || !data.type) return;
  
  if (data.type === 'snapshot') {
    orderbook = { bids: data.bids || [], asks: data.asks || [] };
  } else if (data.type === 'update') {
    // Apply delta update
    updateOrderbookLevel(orderbook.bids, data.bids || []);
    updateOrderbookLevel(orderbook.asks, data.asks || []);
  }
  
  renderOrderbook();
}

function updateOrderbookLevel(levels, updates) {
  updates.forEach(([price, quantity]) => {
    const index = levels.findIndex(([p]) => p === price);
    if (quantity === 0) {
      // Remove level
      if (index > -1) {
        levels.splice(index, 1);
      }
    } else {
      // Update or add level
      if (index > -1) {
        levels[index] = [price, quantity];
      } else {
        levels.push([price, quantity]);
      }
    }
  });
  
  // Sort: bids descending, asks ascending
  levels.sort((a, b) => b[0] - a[0]);
}

function renderOrderbook() {
  const container = document.getElementById('orderbook');
  if (!container) return;
  
  const maxLevels = 15;
  const asks = orderbook.asks.slice(0, maxLevels).reverse();
  const bids = orderbook.bids.slice(0, maxLevels);
  
  let html = '';
  
  // Header
  html += '<div class="grid grid-cols-3 gap-2 px-2 py-2 text-xs text-neutral-400 border-b border-neutral-700">';
  html += '<div>Price</div><div class="text-right">Amount</div><div class="text-right">Total</div>';
  html += '</div>';
  
  // Asks (sells) - red
  asks.forEach(([price, amount]) => {
    const total = (price * amount).toFixed(2);
    html += '<div class="orderbook-row hover:bg-neutral-800">';
    html += `<div class="text-error-500">${price.toFixed(2)}</div>`;
    html += `<div class="text-right">${amount.toFixed(4)}</div>`;
    html += `<div class="text-right text-neutral-400">${total}</div>`;
    html += '</div>';
  });
  
  // Current price
  const currentPrice = bids[0]?.[0] || asks[asks.length - 1]?.[0] || 0;
  html += `<div class="px-2 py-2 text-center font-bold text-lg border-y border-neutral-700 my-1">${currentPrice.toFixed(2)}</div>`;
  
  // Bids (buys) - green
  bids.forEach(([price, amount]) => {
    const total = (price * amount).toFixed(2);
    html += '<div class="orderbook-row hover:bg-neutral-800">';
    html += `<div class="text-success-500">${price.toFixed(2)}</div>`;
    html += `<div class="text-right">${amount.toFixed(4)}</div>`;
    html += `<div class="text-right text-neutral-400">${total}</div>`;
    html += '</div>';
  });
  
  container.innerHTML = html;
}

function handleTradeUpdate(data) {
  const container = document.getElementById('recentTrades');
  if (!container || !data || !data.timestamp || !data.price || !data.quantity) return;
  
  const trade = data;
  const time = new Date(trade.timestamp).toLocaleTimeString();
  const priceClass = trade.side === 'buy' ? 'text-success-500' : 'text-error-500';
  
  const html = `
    <div class="grid grid-cols-3 gap-2">
      <div class="${priceClass}">${trade.price.toFixed(2)}</div>
      <div class="text-right">${trade.quantity.toFixed(4)}</div>
      <div class="text-right text-neutral-400">${time}</div>
    </div>
  `;
  
  container.insertAdjacentHTML('afterbegin', html);
  
  // Keep only recent 50 trades
  while (container.children.length > 50) {
    container.removeChild(container.lastChild);
  }
}

function handleTickerUpdate(data) {
  if (!data || !data.lastPrice) return;
  
  // Update price display
  const currentPriceEl = document.getElementById('currentPrice');
  if (currentPriceEl && data.lastPrice) {
    currentPriceEl.textContent = `$${data.lastPrice.toFixed(2)}`;
  }
  
  const priceChangeEl = document.getElementById('priceChange');
  if (priceChangeEl && typeof data.priceChange24h !== 'undefined') {
    const change = data.priceChange24h;
    priceChangeEl.textContent = `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
    priceChangeEl.className = change >= 0 ? 'badge-success' : 'badge-error';
  }
  
  // Update 24h stats
  if (data.high24h) document.getElementById('high24h').textContent = `$${data.high24h.toFixed(2)}`;
  if (data.low24h) document.getElementById('low24h').textContent = `$${data.low24h.toFixed(2)}`;
  if (data.volume24h) document.getElementById('volume24h').textContent = data.volume24h.toFixed(2);
  if (data.volume24h && data.lastPrice) document.getElementById('volumeUsdt24h').textContent = `$${(data.volume24h * data.lastPrice / 1000000).toFixed(1)}M`;
}

function setupOrderForm() {
  const form = document.getElementById('orderForm');
  const priceInput = document.getElementById('orderPrice');
  const amountInput = document.getElementById('orderAmount');
  const totalInput = document.getElementById('orderTotal');
  
  // Calculate total on price/amount change
  function calculateTotal() {
    const price = parseFloat(priceInput.value) || 0;
    const amount = parseFloat(amountInput.value) || 0;
    const total = price * amount;
    totalInput.value = total.toFixed(2);
  }
  
  priceInput.addEventListener('input', calculateTotal);
  amountInput.addEventListener('input', calculateTotal);
  
  // Percentage buttons
  const percentButtons = document.querySelectorAll('[data-percent]');
  percentButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const percent = parseInt(btn.dataset.percent);
      const available = 10000; // TODO: Get from API
      const price = parseFloat(priceInput.value) || 0;
      
      if (price > 0) {
        const amount = (available * (percent / 100)) / price;
        amountInput.value = amount.toFixed(8);
        calculateTotal();
      }
    });
  });
  
  // Form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    // Show automated trading mode message
    showModal(
      'Automated Trading Mode',
      'Your account is currently in automated trading mode. Manual trading is temporarily disabled.',
      'info'
    );
    
    // Uncomment below to enable actual order placement
    /*
    const orderData = {
      market: currentMarket,
      side: currentSide,
      type: 'limit',
      price: parseFloat(priceInput.value),
      quantity: parseFloat(amountInput.value),
    };
    
    try {
      const response = await fetch(`${API_URL}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(orderData),
      });
      
      if (!response.ok) {
        throw new Error('Order placement failed');
      }
      
      const result = await response.json();
      console.log('Order placed:', result);
      
      // Reset form
      form.reset();
      
      // Show success message
      alert('Order placed successfully!');
    } catch (error) {
      console.error('Order error:', error);
      alert('Failed to place order: ' + error.message);
    }
    */
  });
}

export { currentMarket, orderbook };
